import { takeLatest, all, call, put, select } from 'redux-saga/effects';

// Services
import { userIpsApi } from 'services/apiDigitalTicket/userIps';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Helpers
import { getCompany } from 'helpers/auth';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

import * as UserIpsActions from './actions';
import { UserIpInterface, UserIpsTypes } from './types';
import { GroupInterface } from '../supervisorsGroups/types';

function* list({
  payload,
}: any | ReturnType<typeof UserIpsActions.listUserIpsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando IPs' }));
    }

    const { withoutTransportOperatorId } = payload;
    delete payload.withoutTransportOperatorId;

    const transportOperatorId = getCompany();
    let response;
    if (!withoutTransportOperatorId) {
      response = yield call(userIpsApi.get, {
        params: { transportOperatorId, ...body },
      });
    } else {
      response = yield call(userIpsApi.get, {
        params: { ...body },
      });
    }

    if (response.status === 200) {
      const groupsList = yield select(
        (state) => state.supervisorsGroups.groupsList || [],
      );

      yield put(
        UserIpsActions.listUserIpsSuccess({
          userIpsList: response.data.map((address: UserIpInterface) => ({
            ...address,
            group:
              groupsList.find((gp: GroupInterface) => gp.id === address.owner)
                ?.name || '',
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userIps - list - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserIpsActions.listUserIpsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
  meta,
}: ReturnType<typeof UserIpsActions.getUserIpRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperatorId = getCompany();
    const response = yield call(userIpsApi.get, {
      params: { transportOperatorId, ...payload },
    });

    if (response.status === 200 && response.data.id) {
      yield put(UserIpsActions.getUserIpSuccess({ userIpData: response.data }));
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userIps - get - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserIpsActions.getUserIpFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* filter({
  payload,
}: ReturnType<typeof UserIpsActions.filterUserIpsRequest>): any {
  try {
    const transportOperatorId = getCompany();
    const response = yield call(userIpsApi.get, {
      params: { transportOperatorId, ...payload },
    });

    if (response.status === 200) {
      const groupsList = yield select(
        (state) => state.supervisorsGroups.groupsList || [],
      );

      yield put(
        UserIpsActions.filterUserIpsSuccess({
          userIpsList: response.data.map((address: UserIpInterface) => ({
            ...address,
            group:
              groupsList.find((gp: GroupInterface) => gp.id === address.owner)
                ?.name || '',
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userIps - filter - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserIpsActions.filterUserIpsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof UserIpsActions.createUserIpRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando endereço IP' }));
    const transportOperatorId = getCompany();
    const response = yield call(userIpsApi.create, {
      data: { ...payload, transportOperatorId },
    });

    if (response.status === 200) {
      yield put(UserIpsActions.createUserIpSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Endereço IP criado com sucesso',
        }),
      );

      const { dispatchOnSuccess } = meta;
      if (dispatchOnSuccess) {
        const { action, payload } = dispatchOnSuccess;
        yield put(action(payload));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userIps - create - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserIpsActions.createUserIpFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createList({
  payload,
  meta,
}: ReturnType<typeof UserIpsActions.createListUserIpsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando endereço IP' }));
    const transportOperatorId = getCompany();

    const responses = yield all(
      payload.map((userIp) =>
        call(userIpsApi.create, {
          data: {
            ...userIp,
            transportOperatorId,
          },
        }),
      ),
    );

    if (responses.every((r: any) => r.status === 200)) {
      yield put(UserIpsActions.createListUserIpsSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Endereço IP criado com sucesso',
        }),
      );

      const { dispatchOnSuccess } = meta;
      if (dispatchOnSuccess) {
        const { action, payload } = dispatchOnSuccess;
        yield put(action(payload));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userIps - createList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserIpsActions.createListUserIpsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
  meta,
}: ReturnType<typeof UserIpsActions.updateUserIpRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando endereço IP' }));
    const transportOperatorId = getCompany();
    const response = yield call(userIpsApi.update, {
      data: { ...payload, transportOperatorId },
    });

    if (response.status === 200) {
      yield put(UserIpsActions.updateUserIpSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Endereço IP alterado com sucesso',
        }),
      );

      const { dispatchOnSuccess } = meta;
      if (dispatchOnSuccess) {
        const { action, payload } = dispatchOnSuccess;
        yield put(action(payload));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userIps - update - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserIpsActions.updateUserIpFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
  meta,
}: ReturnType<typeof UserIpsActions.deleteUserIpRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo endereço IP' }),
    );
    const transportOperatorId = getCompany();
    const response = yield call(userIpsApi.delete, {
      params: { transportOperatorId, ...payload },
    });

    if (response.status === 200) {
      yield put(UserIpsActions.deleteUserIpSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Endereço IP excluído com sucesso',
        }),
      );

      const { dispatchOnSuccess } = meta;
      if (dispatchOnSuccess) {
        const { action, payload } = dispatchOnSuccess;
        yield put(action(payload));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userIps - delete - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserIpsActions.deleteUserIpFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const userIpsSagas = [
  takeLatest(UserIpsTypes.LIST_USER_IPS_REQUEST, list),
  takeLatest(UserIpsTypes.GET_USER_IP_REQUEST, get),
  takeLatest(UserIpsTypes.FILTER_USER_IPS_REQUEST, filter),
  takeLatest(UserIpsTypes.CREATE_USER_IP_REQUEST, create),
  takeLatest(UserIpsTypes.CREATE_LIST_USER_IPS_REQUEST, createList),
  takeLatest(UserIpsTypes.UPDATE_USER_IP_REQUEST, update),
  takeLatest(UserIpsTypes.DELETE_USER_IP_REQUEST, remove),
];
